import React from "react";
import { FiArrowLeft, FiInfo, FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import type { Chat } from "../../../../redux/reducers/ChatReducer/ChatSliceTypes";
import { getName } from "../../utils/messageUtils";

interface ChatHeaderProps {
  selectedChat: Chat;
  onBack: () => void;
  onToggleDetails: () => void;
  showDetails: boolean;
  isMobile?: boolean;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  selectedChat,
  onBack,
  onToggleDetails,
  showDetails,
  isMobile = false,
}) => {
  // Get current user
  const { currentUser } = useSelector((state: RootState) => state.user);

  // Get the OTHER user (not the current user)
  const otherUser =
    currentUser?.id === selectedChat?.sender?.id
      ? selectedChat?.receiver
      : selectedChat?.sender;

  const userName = otherUser?.name || "User";
  const userImage = otherUser?.image;

  return (
    <div className="px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm">
      <div className="flex items-center">
        {isMobile && (
          <button
            onClick={onBack}
            className="mr-3 p-2 text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-all"
            aria-label="Back to chat list"
          >
            <FiArrowLeft size={20} />
          </button>
        )}

        <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
          {userImage ? (
            <img
              src={userImage}
              alt={userName}
              className="w-full h-full rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-600"
            />
          ) : (
            <div className="w-full h-full rounded-full bg-gradient-to-br from-[#F56948] to-[#E5593D] flex items-center justify-center text-white font-bold text-lg">
              {userName?.charAt(0)?.toUpperCase() || "?"}
            </div>
          )}
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full ring-2 ring-white dark:ring-neutral-800"></div>
        </div>

        <div className="ml-3 sm:ml-4">
          <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-white">
            {getName(userName)}
          </h3>
          {selectedChat?.property && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              📍{" "}
              {(selectedChat.property as any).slug
                ?.split("-")
                .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ")}
            </p>
          )}
        </div>
      </div>

      <button
        onClick={onToggleDetails}
        className="p-2.5 text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-xl transition-all"
        aria-label={showDetails ? "Hide details" : "Show details"}
      >
        {showDetails ? <FiX size={20} /> : <FiInfo size={20} />}
      </button>
    </div>
  );
};

export default ChatHeader;
